import { useMemo, useState, useEffect } from 'react';
import { Col, Form, Row } from 'antd';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from 'components';
import { DeleteIcon, EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import { EyeOutlined } from '@ant-design/icons';
import { useApi } from 'hooks/useApi';
import InterviewsApi from 'libs/api/interviews';
import { interviewsAdapter } from './interviews.adapter';
import { useRecoilValue } from 'recoil';
import { getPage, getShoppingCenters } from 'hooks/useUser';
import UIHelper from 'helpers/UIHelper';
import ErrorHelper from 'helpers/ErrorHelper';
import moment from 'moment';
import Helpers from 'helpers';
import { getFilters } from 'hooks/useShops';

const api = new InterviewsApi();

const useInterviews = (
  isMainPage = true,
  isEdit,
  getAnswers,
  getListCompany,
  answersEditPage,
) => {
  let { id, idCompany } = useParams();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const filtersData = useRecoilValue(getFilters);
  const [typeInterview, setTypeInterview] = useState('');
  const [answerCheckboxes, setAnswerCheckboxes] = useState({});
  const [listCompany, setListCompany] = useState([]);
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const pages = useRecoilValue(getPage);
  const [answers, setAnswers] = useState({});
  const {
    data,
    loading,
    handleDownloadExcel,
    onChangeTableFilter,
    handleChangeFilters,
    onDelete,
    setLoading,
    setUpdateInProcess,
    updateInProcess,
    singleItem,
  } = useApi({
    resourceKey: 'interviews',
    singleResourceKey: 'interview',
    api,
    isMainPage,
    adapter: interviewsAdapter,
  });

  const handleCreateOrUpdateCategory = async (values) => {
    setUpdateInProcess(true);
    try {
      const data = {
        interview: {
          ...values,
        },
      };
      if (!id) {
        await api.create({ data });
        navigate(UIHelper.getListRoute(pathname, 1));
      } else {
        await api.put(id, { data });
        navigate(UIHelper.getListRoute(pathname, 2));
      }
    } catch (e) {
      form.setFields(
        ErrorHelper.getErrorsForForm(e?.response?.data || {}, {
          shopping_center: 'shopping_center_id',
        }),
      );
    } finally {
      setUpdateInProcess(false);
    }
  };

  const columns = useMemo(() => {
    return [
      {
        title: t('Outlet Village'),
        dataIndex: 'shopping_centers.title',
        fixed: 'left',
        sorter: () => { },
      },
      {
        title: t('Titolo'),
        dataIndex: 'title',
        sorter: () => { },
      },
      {
        title: t('Data inizio'),
        dataIndex: 'date_from',
        sorter: () => { },
        render: (data) => moment(data).format('DD MMMM YYYY'),
      },
      {
        title: t('Data fine'),
        dataIndex: 'date_to',
        sorter: () => { },
        render: (data) => moment(data).format('DD MMMM YYYY'),
      },
      {
        title: t('DESTINATARI'),
        dataIndex: 'type_interview',
        sorter: () => { },
        render: (data) => {
          switch (data) {
            case 'type_interview_clients':
              return t('Clienti');
            case 'type_interview_company':
              return t('INSEGNE');
            default:
              return '';
          }
        },
      },
      {
        title: t('Insegne/clienti che hanno risposto'),
        dataIndex: 'count_answers',
      },
      {
        title: '',
        dataIndex: 'id',
        align: 'right',
        width: 150,
        fixed: 'right',
        render: (data, row) => (
          <Row
            gutter={[16, 16]}
            justify={'left'}
            onClick={(e) => e.stopPropagation()}
          >
            {row.can_link_to_interviews && (
              <Col>
                <Link to={`${pathname}/answers/${row.id}/new`}>
                  <Button icon={<EyeOutlined />}>{t('Apri')}</Button>
                </Link>
              </Col>
            )}
            {row.can_result && (
              <Col>
                <Link to={`${pathname}/answers/${row.id}`}>
                  <Button type={'green'}>{t('Risultati')}</Button>
                </Link>
              </Col>
            )}
            {row.can_company_invite && (
              <Col>
                <Link to={`${pathname}/${row.id}/list_company`}>
                  <Button type={'green'}>{t('Insegne invitate')}</Button>
                </Link>
              </Col>
            )}
          </Row>
        ),
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 160,
        fixed: 'right',
        render: (data, row) =>
          (pages?.ability?.can_read !== false ||
            row.can_read !== false ||
            row.can_update ||
            row.can_destroy) && (
            <Row
              gutter={16}
              justify={'end'}
              onClick={(e) => e.stopPropagation()}
            >
              {(pages?.ability?.can_read !== false ||
                row.can_read !== false) && (
                <Col>
                  <Link to={`${pathname}/${row.id}`}>
                    <Button type={'secondary'} icon={<EyeOutlined />} />
                  </Link>
                </Col>
              )}
              {row.can_update && (
                <Col>
                  <Link to={`${pathname}/${row.id}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Col>
              )}
              {row.can_destroy && (
                <Col>
                  <Link to={`${pathname}/${row.id}/delete`}>
                    <Button type={'red'} icon={<DeleteIcon />} />
                  </Link>
                </Col>
              )}
            </Row>
          ),
      },
    ];
  }, [pathname, t, pages?.ability?.can_read]);

  const filters = useMemo(
    () => [
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'by_shopping_center',
        values: shoppingCenters.map((item) => ({
          title: item.title,
          value: item.id,
        })),
      },
      {
        title: t('Date range'),
        type: 'range',
        name: 'by_date_from',
        values: [],
        keys: ['by_date_from', 'by_date_to'],
      },
      {
        title: t('Cerca'),
        type: 'input',
        name: 'by_title',
        values: [],
        isActiveSearch: true,
      },
      {
        title: t('DESTINATARI'),
        type: 'select',
        name: 'by_type_interview',
        values: [
          { value: 'type_interview_company', title: t('INSEGNE') },
          { value: 'type_interview_clients', title: t('Clienti') },
        ],
      },
    ],
    [t, shoppingCenters],
  );

  const columnsListCompany = useMemo(() => {
    return [
      {
        title: t('Titolo'),
        dataIndex: 'name',
        sorter: () => { },
      },
      {
        title: t('Utente'),
        dataIndex: 'user',
        render: (data) => data?.short_name || '',
      },
    ];
  }, [t]);

  useEffect(() => {
    (async () => {
      if (id) {
        try {
          if (isEdit) {
            let fieldsValues = [];
            for (let key in singleItem) {
              switch (key) {
                case 'date_to':
                case 'date_from': {
                  if (singleItem[key]) {
                    fieldsValues.push({
                      name: key,
                      value: moment(singleItem[key], 'YYYY-MM-DD'),
                    });
                  }
                  break;
                }
                case 'shopping_center': {
                  fieldsValues.push({
                    name: 'shopping_center_id',
                    value: singleItem[key].id,
                  });
                  break;
                }
                case 'company_categories': {
                  fieldsValues.push({
                    name: 'company_category_ids',
                    value: singleItem[key].map((item) => item.id),
                  });
                  break;
                }
                case 'interview_questions': {
                  fieldsValues.push({
                    name: 'interview_questions_attributes',
                    value: singleItem[key],
                  });
                  break;
                }
                default: {
                  fieldsValues.push({
                    name: key,
                    value: singleItem[key],
                  });
                }
              }
            }
            setTypeInterview(singleItem.type_interview);
            form.setFields(fieldsValues);
          }
        } catch (e) {
          console.log(e);
        }
      }
    })();
  }, [singleItem, form, id, isEdit]);

  useEffect(() => {
    (async () => {
      if (id && getAnswers) {
        try {
          const { data } = await api.getAnswer(id);
          setAnswers(data);
        } catch (e) {
          console.log(e);
        }
      }
    })();
  }, [getAnswers, id]);

  useEffect(() => {
    (async () => {
      if (id && getListCompany) {
        try {
          const {
            data: { companies },
          } = await api.get(`${id}/list_company`, {
            params: { per: 10000 },
          });
          setListCompany(companies);
        } catch (e) {
          console.log(e);
        }
      }
    })();
  }, [id, getListCompany]);

  const handleDownloadExcelSingle = async () => {
    setLoading(true);
    try {
      const response = await api.getExcelSingle(id);
      Helpers.downloadFile(response, 'file.xlsx');
    } catch (e) {
      const error = e?.response?.data?.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerQuestions = async (values) => {
    setUpdateInProcess(true);
    try {
      const data = {
        data: {
          interview_id: id,
          question: {
            ...values,
          },
        },
      };
      await api.answerQuestions(id, data, idCompany);
      navigate(UIHelper.getListRoute(pathname, 3));
    } catch (e) {
      UIHelper.openNotificationWithIcon(
        'error',
        e?.response?.data?.base || t('Something went wrong!'),
      );
    } finally {
      setUpdateInProcess(false);
    }
  };

  return {
    columns,
    data,
    filters,
    loading,
    columnsListCompany,
    handleDownloadExcel,
    onChangeTableFilter,
    handleChangeFilters,
    onDelete,
    updateInProcess,
    form,
    shoppingCenters,
    handleCreateOrUpdateCategory,
    typeInterview,
    setTypeInterview,
    companyCategories: filtersData?.company_categories,
    answerCheckboxes,
    setAnswerCheckboxes,
    answers,
    listCompany,
    handleDownloadExcelSingle,
    singleItem,
    handleAnswerQuestions,
  };
};

export default useInterviews;
